// Conversión de una imagen (logo del comercio) a un comando ESC/POS raster
// monocromo (GS v 0), para imprimir el logo en el ticket Bluetooth.
//
// Usa <canvas> del navegador: carga la imagen, la escala al ancho del rollo, la
// umbraliza a 1 bit (blanco/negro) y empaqueta los bits en el formato GS v 0.
//
// Es BEST-EFFORT: si la imagen no carga o el canvas queda "tainted" por CORS
// (`getImageData` lanza), devuelve null y el ticket se imprime SIN logo (no se
// rompe la impresión). No se accede a `document`/`Image` en el top-level.

// Ancho en puntos del cabezal según el rollo (estándar: 8 dots/mm efectivos).
export const dotsForWidth = (pageWidth: 58 | 80 | undefined): number => (pageWidth === 58 ? 384 : 576);

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo cargar la imagen del logo.'));
    img.src = url;
  });

// Devuelve el comando GS v 0 (raster) del logo escalado a `maxWidthDots`, o null
// si la imagen no es accesible/convertible.
export const imageToEscPosRaster = async (url: string, maxWidthDots: number): Promise<Uint8Array | null> => {
  if (typeof document === 'undefined' || typeof Image === 'undefined') return null;
  try {
    const img = await loadImage(url);
    const ratio = Math.min(1, maxWidthDots / (img.width || maxWidthDots));
    const w = Math.max(1, Math.round((img.width || maxWidthDots) * ratio));
    const h = Math.max(1, Math.round((img.height || 1) * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h); // lanza si está tainted por CORS
    const bytesPerRow = Math.ceil(w / 8);
    const raster = new Uint8Array(bytesPerRow * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        const alpha = data[i + 3];
        // Transparente → blanco; si no, luminancia. < 128 ⇒ pixel negro.
        const lum = alpha < 16 ? 255 : 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        if (lum < 128) raster[y * bytesPerRow + (x >> 3)] |= 0x80 >> (x & 7);
      }
    }
    const xBytes = bytesPerRow;
    const header = new Uint8Array([
      0x1d,
      0x76,
      0x30,
      0x00, // GS v 0 m=0
      xBytes & 0xff,
      (xBytes >> 8) & 0xff, // ancho en bytes
      h & 0xff,
      (h >> 8) & 0xff, // alto en puntos
    ]);
    const out = new Uint8Array(header.length + raster.length);
    out.set(header);
    out.set(raster, header.length);
    return out;
  } catch {
    return null;
  }
};
